import { ChannelReference } from "@commercetools/platform-sdk"
import {
  ErrorCard as _ErrorCard,
  Label2,
  ParagraphSizeEnum,
  PrimaryButton,
  TechnicalParagraph
} from "@sixty-six-north/ui-system"
import { Form, Formik } from "formik"
import { useStoreContext } from "i18n/StoreHooks"
import { RegisterInterestRequest } from "pages/api/apprise/interest"
import {
  AgreeCheckBox,
  EmailAddress,
  FirstName,
  LastName,
  SignupCheckBox,
  formStatus
} from "prismic/slices"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Flex, Grid } from "theme-ui"
import * as Yup from "yup"
import { countryToStore } from "../../cart/I18NStores"
import { useCommerceToolsStore } from "../../i18n/StoreProvider"
import { useRecaptcha } from "../../recaptcha/RecaptchaHooks"
import { SmallProductCard } from "../product/SmallProductCard"

interface FormData {
  firstName: string
  lastName: string
  email: string
  signUpCheckbox: boolean
  agreeCheckbox: boolean
}

const validateFormData = (
  t: (key: string) => string,
  validateNewsletterSignup: boolean
) => {
  return Yup.object().shape({
    firstName: Yup.string().required(t("forms:firstNameRequired")),
    lastName: Yup.string().required(t("forms:lastNameRequired")),
    email: Yup.string()
      .email(t("login:validEmail"))
      .required(t("registration:emailIsRequired")),
    signUpCheckbox: validateNewsletterSignup
      ? Yup.bool().oneOf([true], t("forms:signUpRequired"))
      : Yup.bool().notRequired(),
    agreeCheckbox: Yup.bool().oneOf([true], t("forms:agreeRequired"))
  })
}

export interface NotifyBackInStockProps {
  product: {
    sku: string
    productName: string
    productDescription: string
    productColor: string
    productSize: string
    productPrice: string
    productImageUrl: string
    productImageTitle?: string
  }
}

export function NotifyBackInStock({ product }: NotifyBackInStockProps) {
  const { t } = useTranslation()
  const store = useStoreContext()
  const commerceToolsStoreContext = useCommerceToolsStore()
  const recaptcha = useRecaptcha()
  const storeCountry = store.country.toUpperCase()
  const country = store.isShoppingInEurope() ? "" : storeCountry
  const [isLoading, setLoading] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)
  const currentStoreName = countryToStore(store.country)?.name || ""

  async function onSubmit(values: FormData) {
    setLoading(true)

    const { email, firstName, lastName, signUpCheckbox } = values
    const recaptchaPromise = recaptcha("subscribe")

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const productURL = `${window.location.origin}${
      window.location.pathname
    }?c=${urlParams.get("c")}`

    const request: RegisterInterestRequest = {
      firstName,
      lastName,
      locale: storeCountry,
      sku: product.sku,
      stores: [currentStoreName],
      supplyChannels:
        commerceToolsStoreContext?.supplyChannels?.map(
          (it: ChannelReference) => it.id
        ) || [],
      emailAddress: email,
      country,
      subscribe: signUpCheckbox,
      productURL,
      productName: product.productName,
      productImageURL: product.productImageUrl
    }

    return fetch("/api/apprise/interest", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        meta: { recaptchaToken: await recaptchaPromise },
        ...request
      })
    }).then(() => setIsSuccessful(true))
  }

  return (
    <Flex
      sx={{
        flexDirection: "column"
      }}
    >
      <SmallProductCard
        firstRowTitle={product.productName}
        firstRowDescription={product.productDescription}
        secondRowTitle={t("product:color")}
        secondRowDescription={product.productColor}
        thirdRowTitle={t("checkout:size")}
        thirdRowDescription={product.productSize}
        productImageUrl={product.productImageUrl}
        productImageTitle={product.productImageTitle}
      />
      {isSuccessful ? (
        <>
          <Flex
            sx={{
              flexDirection: "column",
              mt: 24,
              mb: 16
            }}
          >
            <Label2
              sx={{
                fontWeight: "bold"
              }}
            >
              {t("forms:newsLetterThankYou")}
            </Label2>
            <TechnicalParagraph variant={ParagraphSizeEnum.small}>
              {t("product:notifyBackInStockSuccessMessage")}
            </TechnicalParagraph>
          </Flex>
        </>
      ) : (
        <>
          <Flex
            sx={{
              flexDirection: "column",
              mt: 24,
              mb: 16
            }}
          >
            <Label2
              sx={{
                fontWeight: "bold"
              }}
            >
              {t("product:notifyBackInStockHeading")}
            </Label2>
            <TechnicalParagraph variant={ParagraphSizeEnum.small}>
              {t("product:notifyBackInStockText")}
            </TechnicalParagraph>
          </Flex>
          <Formik
            initialValues={{
              firstName: "",
              lastName: "",
              email: "",
              signUpCheckbox: false,
              agreeCheckbox: false
            }}
            onSubmit={onSubmit}
            validationSchema={validateFormData(t, false)}
          >
            {props => (
              <Form>
                {formStatus(props, "error") && (
                  <Box sx={{ marginBottom: 16 }}>
                    <_ErrorCard
                      title={t("forms:formSubmitError")}
                      message={t("forms:formSubmitErrorMessage")}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    "& > * + div": { mt: 8 }
                  }}
                >
                  <Grid columns={2} gap={8}>
                    <FirstName />
                    <LastName />
                  </Grid>
                  <Box>
                    <EmailAddress name="email" />
                  </Box>

                  <Box sx={{ pt: 16 }}>
                    <SignupCheckBox />
                  </Box>
                  <Box sx={{ pt: 16, pb: 8 }}>
                    <AgreeCheckBox />
                  </Box>

                  <Box>
                    <PrimaryButton
                      type="submit"
                      fill={false}
                      loading={isLoading}
                      id="submitFormButton"
                    >
                      {t("registration:submit", "Submit")}
                    </PrimaryButton>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Flex>
  )
}
